import { useId } from "react";
import { ImageOff, ZoomIn } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type {
  EchoflicksSessionMedia,
  ImageTriageObservation,
} from "../../types";
import { useDisplayableImage } from "../useDisplayableImage";
import { findingCopy, findingTooltip, rankFindings } from "./imageTriageCopy";

export function ImageTriageThumb({
  observation,
  media,
  isReference = false,
  onOpen,
}: {
  observation: ImageTriageObservation;
  media?: EchoflicksSessionMedia | null;
  isReference?: boolean;
  onOpen?: () => void;
}) {
  const image = useDisplayableImage(media);
  const descriptionId = useId();
  const visibleFindings = rankFindings(observation.findings).slice(0, 2);
  const overflow = Math.max(
    0,
    observation.findings.length - visibleFindings.length,
  );
  const accessibleDescription = observation.findings.length
    ? observation.findings.map(findingTooltip).join(" ")
    : "No configured image-preprocessing signal was triggered.";

  return (
    <div
      className="min-w-0 space-y-2"
      data-testid={`image-triage-thumb-${observation.occurrence_id}`}
    >
      <div className="relative overflow-hidden rounded-lg border bg-muted/30">
        <button
          type="button"
          className="group relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-default"
          onClick={onOpen}
          disabled={!media || !onOpen}
          aria-label={
            media
              ? "Open image in gallery"
              : "Image unavailable in media gallery"
          }
          aria-describedby={descriptionId}
        >
          {image.url ? (
            <img
              src={image.url}
              alt=""
              className="size-full object-cover transition-transform group-hover:scale-[1.02] motion-reduce:transition-none"
              onError={image.refresh}
            />
          ) : (
            <div className="flex flex-col items-center gap-2 px-3 text-center text-xs">
              <ImageOff className="size-5" aria-hidden />
              <span>
                {image.isLoading ? "Preparing preview…" : "Preview unavailable"}
              </span>
            </div>
          )}
          {media ? (
            <span className="pointer-events-none absolute right-2 top-2 rounded-full bg-black/65 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none">
              <ZoomIn className="size-4" aria-hidden />
            </span>
          ) : null}
        </button>

        {isReference ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="absolute left-2 top-2 rounded-md border bg-background/95 px-2 py-1 text-[11px] font-medium shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Reference thumbnail explanation"
              >
                Reference thumbnail
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-72">
              Used to label this group. The enforcement status above shows what
              reached the director.
            </TooltipContent>
          </Tooltip>
        ) : null}

        {visibleFindings.length || overflow ? (
          <div className="absolute inset-x-2 bottom-2 flex flex-wrap gap-1">
            {visibleFindings.map((finding) => {
              const copy = findingCopy(finding);
              return (
                <Tooltip key={`${finding.code}-${finding.detector_version}`}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className={`rounded-md border px-1.5 py-0.5 text-[11px] font-medium shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring ${copy.tone}`}
                      aria-label={`${copy.badge}: ${findingTooltip(finding)}`}
                    >
                      {copy.badge}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-80">
                    {findingTooltip(finding)}
                  </TooltipContent>
                </Tooltip>
              );
            })}
            {overflow ? (
              <Badge
                variant="secondary"
                className="px-1.5 text-[11px]"
                aria-label={`${overflow} more findings`}
              >
                +{overflow}
              </Badge>
            ) : null}
          </div>
        ) : null}
      </div>
      <p id={descriptionId} className="sr-only">
        {accessibleDescription}
      </p>
      <p className="truncate font-mono text-[11px] text-muted-foreground">
        Upload {observation.source.upload_sequence + 1}
      </p>
    </div>
  );
}
